/**
 * Shared property types for canonical events.
 * Based on the User-Events Taxonomy "Required properties" + "Nice-to-have"
 * columns. Inherits BaseDims to ensure every event carries identity +
 * session + device context.
 */

export type Platform = 'web' | 'mobile' | 'hexa' | 'backend';

export interface BaseDims {
  /** uuid v4 generated at emit time, used for vendor-side dedup */
  event_id?: string;
  /** ISO-8601 UTC timestamp */
  event_time?: string;
  /** authenticated member id, or pseudonymised anonymous id */
  user_id?: string;
  /** anonymous device-stable identifier (localStorage) */
  device_id?: string;
  /** rotates on session expiry (~30min inactivity) */
  session_id?: string;
  platform?: Platform;
  app_version?: string;
  locale?: string;
  country?: string;
  /** current pathname when event fired */
  page?: string;
  /** derived page group, e.g. "learn", "watch", "discover" */
  page_group?: string;
}

export interface ContentIdentity {
  content_id: string;
  content_type?: string;
  is_ugc?: boolean;
  creator_id?: string;
}

// ---- Account
export interface AccountCreateStartedProps {
  entry_point?: string;
  is_organic?: boolean;
  campaign_id?: string;
}

export interface AccountInfoSubmittedProps {
  fields_group: string;
  profile_completion_rate?: number;
}

export interface AccountUpdatedProps {
  updated_field_group: string;
  source_screen?: string;
}

// ---- Content
export interface ContentImpressionProps extends ContentIdentity {
  placement: string;
  position_bucket?: string;
  slider_name?: string;
  content_source?: string;
}

export interface ContentStartedProps extends ContentIdentity {
  player_type?: string;
  start_threshold?: number;
  is_flux?: boolean;
  autoplay?: boolean;
}

export interface ContentCompletedProps extends ContentIdentity {
  completion_threshold?: number;
}

export interface ContentSharedProps extends ContentIdentity {
  share_channel?: string;
}

export interface WatchProgressProps extends ContentIdentity {
  progression: number;
  seconds_watched: number;
  heartbeat_time?: number;
}

export interface ContentDetailViewedProps extends ContentIdentity {
  placement?: string;
}

export interface PlayerClosedProps extends ContentIdentity {
  progress_bucket?: string;
  close_context?: string;
}

// ---- Engagement
export interface BookmarkAddedProps extends ContentIdentity {
  placement?: string;
}

export interface BookmarkRemovedProps extends ContentIdentity {}

export interface CreatorFollowedProps {
  creator_id: string;
}

export interface InterestsSelectedProps {
  interest_count_bucket: string;
  interest_group_set?: string;
}

export interface LanguageChangedProps {
  new_language: string;
  old_language?: string;
}

// ---- Navigation
export interface CTAClickedProps {
  cta_id: string;
  cta_name: string;
  cta_type: string;
  page_name?: string;
  content_id?: string;
  placement?: string;
}

export interface PageViewedProps {
  page_name: string;
  page_group?: string;
  entry_source?: string;
}

export interface DeeplinkOpenedProps {
  campaign_id?: string;
  deeplink_type?: string;
}

export interface MenuClickedProps {
  menu_item: string;
  page_name?: string;
}

// ---- Subscription
export type BillingProvider = 'stripe' | 'apple' | 'google' | 'huawei' | 'other';

export interface PaywallViewedProps {
  content_id?: string;
  paywall_context?: string;
  placement?: string;
}

export interface GoToSubscriptionProps {
  paywall_context?: string;
  selected_offer_type?: string;
}

export interface SubscriptionCheckoutStartedProps {
  billing_provider: BillingProvider;
  offer_type: string;
  billing_period: string;
  store?: string;
  currency?: string;
}

// ---- Search
export interface SearchCompletedProps {
  query: string;
  result_count: number;
  latency?: number;
}

export interface SearchResultClickedProps {
  query: string;
  result_count: number;
  rank: number;
  content_id: string;
}

export interface SearchStartedProps {
  search_context?: string;
  query_length_bucket?: string;
}

// ---- CRM
export interface EmailClickedProps {
  campaign_id?: string;
  canvas_id?: string;
  email_type?: string;
}

export interface NotificationOpenedProps {
  campaign_id?: string;
  notification_type?: string;
}

// ---- Technical
export interface ErrorEncounteredProps {
  error_code_bucket: string;
  error_surface: string;
  content_id?: string;
  network_bucket?: string;
}

// ---- Interactive
export interface QuizStartedProps {
  quiz_type: string;
}

export interface QuizCompletedProps {
  quiz_type: string;
  result_bucket?: string;
}

// ---- Identify traits (Amplitude user properties + Braze custom attributes)
export interface UserTraits {
  email?: string;
  first_name?: string;
  last_name?: string;
  country?: string;
  language?: string;
  subscription_type?: string;
  user_status?: string;
  account_creation_date?: string;
  first_subscription_date?: string;
  app_opening_channel?: string;
  interests?: string[];
  [extra: string]: unknown;
}

// ---- Revenue (for purchase events)
export interface RevenueProps {
  product_id: string;
  price: number;
  quantity?: number;
  currency?: string;
  revenue_type?: 'subscription' | 'one_time';
  receipt?: string;
}

/** Any event payload — adapters accept this loose shape. */
export type TrackingPayload = Record<string, unknown> & Partial<BaseDims>;
